import { BranchSummary, Company } from "@/domain";
import * as error from "@/domain/error";
import { getUserStartPage } from "@/modules/router";
import { ValidationRule } from "@/util";
import * as debug from "@/util/debug";
import VueRouter, { RawLocation } from "vue-router";
import { Store } from "vuex";

export function toErrorMsg(err: Partial<error.AppError> | null | undefined | unknown, defaultText: string): string {
  return error.utils.GetCodeDescription(err as Error, defaultText);
}

// Firebase requires a verified email before enrolling a second factor - this is the
// only MFA failure that needs more than a message (see isMfaUnverifiedEmailError).
export function isMfaUnverifiedEmailError(err: unknown): boolean {
  const message = (err as Error)?.message;
  return !!message && (message.includes("UNVERIFIED_EMAIL") || message.toLowerCase().includes("verify email first"));
}

// MFA operations (enroll/sign-in) often fail with codes that have no user-friendly
// mapping - fall back to the real error message instead of hiding it behind a
// generic default, and give a specific hint for the "recent login required" case.
export function toMfaErrorMsg(err: unknown, lang: { [key: string]: string }): string {
  const message = (err as Error)?.message;
  if (message?.includes("CREDENTIAL_TOO_OLD_LOGIN_AGAIN")) {
    return lang.mfaCredentialTooOld;
  }
  if (isMfaUnverifiedEmailError(err)) {
    return lang.mfaUnverifiedEmail;
  }
  return error.utils.GetCodeDescription(err as Error, message || lang.errorMsgLogin);
}

export function rulesRunner(vueComponent: {
  rules: ValidationRule[];
  value: unknown;
  hasError: boolean;
}): ValidationRule[] {
  return [
    (/*val: any*/) => {
      for (const r of vueComponent.rules || []) {
        const result = r(vueComponent.value);
        if (typeof result === "string") {
          vueComponent.hasError = true;
          return result;
        }
      }
      vueComponent.hasError = false;
      return true;
    }
  ];
}

export async function navigateToTerminal(
  vueComp: { $router: VueRouter; $store: Store<unknown> },
  page: null | "vending" | "info" | "transactions",
  company: Company,
  branch?: BranchSummary | null,
  tcod?: string | null,
  code?: string | null,
  q?: string | null
) {
  await vueComp.$store.dispatch("selectCompany", company);
  let finalPage = "";
  switch (page) {
    case "vending":
      finalPage = "/vendingConfig";
      break;
    case "info":
      finalPage = "/companyInfo";
      break;
    case "transactions":
      finalPage = "/transactionquery";
      break;
    default:
      finalPage = getUserStartPage();
  }
  const query: Record<string, string> = {
    companyID: company.id.toString()
  };
  if (branch) {
    query.branchID = branch.id.toString();
    if (q) query.q = q;
    if (tcod) query.tcod = tcod;
    if (code) query.terminalID = code;
  }
  routePush(vueComp, {
    path: finalPage,
    query: query
  });
}

export function routePush(vueComponent: { $router: VueRouter }, route: RawLocation) {
  debug.promiseEnd(vueComponent.$router.push(route));
}

export function isIE11() {
  let rv = -1;
  if (navigator.appName === "Microsoft Internet Explorer") {
    const ua = navigator.userAgent;
    const re = new RegExp("MSIE ([0-9]{1,}[\\.0-9]{0,})");
    if (re.exec(ua) !== null) {
      rv = parseFloat(RegExp.$1);
    }
  } else if (navigator.appName === "Netscape") {
    const ua = navigator.userAgent;
    const re = new RegExp("Trident/.*rv:([0-9]{1,}[\\.0-9]{0,})");
    if (re.exec(ua) !== null) {
      rv = parseFloat(RegExp.$1);
    }
  }
  return rv === 11;
}
